import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  UserCircleIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { accountsAPI, oauthAPI } from '../services/api';
import { isInAppBrowser, isAndroid, openInExternalBrowser, copyToClipboard, getInAppBrowserType } from '../utils/browserDetection';

const InstagramAccountsManager = ({ selectedAccountId, onAccountSelect }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showAccountTypeModal, setShowAccountTypeModal] = useState(false);
  const [showInAppBrowserModal, setShowInAppBrowserModal] = useState(false);
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showGoogleDriveRequiredModal, setShowGoogleDriveRequiredModal] = useState(false);
  const [showInstagramExplanationModal, setShowInstagramExplanationModal] = useState(false);

  // Fetch Instagram accounts
  const { data: accountsData, isLoading, error } = useQuery({
    queryKey: ['instagram-accounts'],
    queryFn: () => accountsAPI.getInstagramAccounts().then(res => res.data),
    retry: 1,
    onError: (error) => {
      console.error('Failed to fetch Instagram accounts:', error);
    }
  });

  // Fetch OAuth status - needed for Google Drive check and fallback if accounts API fails
  const { data: oauthStatus } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => oauthAPI.getStatus().then(res => res.data),
  });

  // Disconnect account mutation
  const disconnectMutation = useMutation({
    mutationFn: (accountId) => accountsAPI.disconnectInstagramAccount(accountId),
    onSuccess: () => {
      toast.success('Instagram account disconnected');
      queryClient.invalidateQueries(['instagram-accounts']);
      queryClient.invalidateQueries(['oauth-status']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to disconnect account');
    },
  });

  // Refresh profile mutation
  const refreshMutation = useMutation({
    mutationFn: (accountId) => accountsAPI.refreshInstagramProfile(accountId),
    onSuccess: (data) => {
      toast.success('Profile refreshed successfully');
      queryClient.invalidateQueries(['instagram-accounts']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to refresh profile');
    },
  });

  // Show the account type selection modal
  const handleConnectInstagramClick = () => {
    if (!can_connect_more) {
      toast.error(`Account limit reached. You can connect up to ${limit} account(s) with your ${subscription_tier} plan.`);
      return;
    }
    // Check if Google Drive is connected first
    if (!oauthStatus?.google_connected) {
      setShowGoogleDriveRequiredModal(true);
      return;
    }
    setShowAccountTypeModal(true);
  };

  // Show explanation modal after account type is confirmed
  const handleConnectInstagram = async (accountType = 'business') => {
    setShowAccountTypeModal(false);
    // Store account type and show explanation modal
    sessionStorage.setItem('pending_instagram_account_type', accountType);
    setShowInstagramExplanationModal(true);
  };

  // Proceed with Instagram OAuth after user confirms
  const proceedWithInstagramOAuth = async () => {
    setShowInstagramExplanationModal(false);
    const accountType = sessionStorage.getItem('pending_instagram_account_type') || 'business';
    sessionStorage.removeItem('pending_instagram_account_type');
    
    // Store connection attempt for abandoned flow recovery
    localStorage.setItem('instagram_connection_attempted', Date.now().toString());
    
    // Check if user is in an in-app browser (Instagram/Facebook)
    // These browsers have known OAuth issues with very low success rates
    if (isInAppBrowser()) {
      if (isAndroid()) {
        // On Android, try to open in Chrome using intent URL
        const opened = openInExternalBrowser(window.location.href);
        if (opened) {
          return; // Chrome should open, user will reconnect from there
        }
      }
      // On iOS or if Android intent failed, show the instruction modal
      setShowInAppBrowserModal(true);
      return;
    }
    
    setIsConnecting(true);
    try {
      const response = await oauthAPI.getInstagramAuthUrl(accountType);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect Instagram');
      setIsConnecting(false);
    }
  };

  // Handle "I have a personal account" - show info modal about switching to Creator
  const handlePersonalAccountHelp = async () => {
    setShowAccountTypeModal(false);
    // Log this click for analytics
    try {
      await oauthAPI.logPersonalAccountClick();
    } catch (e) {
      // Ignore errors - logging shouldn't block navigation
    }
    setShowPersonalInfoModal(true);
  };

  // Proceed to Instagram OAuth after user acknowledges they need to switch to Creator
  const handlePersonalAccountContinue = () => {
    setShowPersonalInfoModal(false);
    handleConnectInstagram('business'); // Uses same OAuth flow
  };

  // Handle connecting Google Drive
  const handleConnectGoogle = async () => {
    try {
      const response = await oauthAPI.getGoogleAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect Google Drive');
    }
  };

  const handleAccountSelect = (accountId) => {
    if (onAccountSelect) {
      onAccountSelect(accountId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Handle fallback data when accounts API fails
  let accounts = [];
  let limit = 1;
  let can_connect_more = false;
  let remaining_slots = 0;
  let subscription_tier = user?.subscription_tier || 'free';
  
  if (accountsData) {
    // Use real data from accounts API
    accounts = accountsData.accounts || [];
    limit = accountsData.limit;
    can_connect_more = accountsData.can_connect_more;
    remaining_slots = accountsData.remaining_slots;
    subscription_tier = accountsData.subscription_tier;
  } else if (error && oauthStatus?.instagram_connected) {
    // Fallback: create basic account data from OAuth status
    accounts = [{
      id: 1,
      username: oauthStatus.instagram_username || 'Instagram Account',
      display_name: null,
      profile_picture_url: null,
      follower_count: null,
      following_count: null,
      posts_count: null,
      synced_posts_count: 0,
      backed_up_posts_count: 0,
      connected_at: null,
      last_sync: null,
      token_expires_at: null
    }];
    
    // Calculate limits based on subscription tier
    const tierLimits = {
      free: 1,
      premium: 1,
      pro: 3,
      agency: 10
    };
    limit = tierLimits[subscription_tier] || 1;
    remaining_slots = Math.max(0, limit - accounts.length);
    can_connect_more = remaining_slots > 0;
  }

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-white">Instagram Accounts</h3>
          <p className="text-sm text-gray-300 mt-1">
            {accounts.length} of {limit} account{limit !== 1 ? 's' : ''} connected
            {subscription_tier === 'pro' && remaining_slots > 0 && (
              <span className="ml-2 text-green-400">• {remaining_slots} slot{remaining_slots !== 1 ? 's' : ''} available</span>
            )}
          </p>
        </div>
        
        {can_connect_more && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleConnectInstagramClick}
              disabled={isConnecting || !oauthStatus?.google_connected}
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white ${
                oauthStatus?.google_connected
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 shadow-lg hover:shadow-xl transform hover:scale-105'
                  : 'bg-gray-600 cursor-not-allowed opacity-60'
              } disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            >
              {isConnecting ? (
                <>
                  <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Connecting...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add Account
                </>
              )}
            </button>
            <div className="flex items-center px-2.5 py-1.5 bg-green-500/10 rounded-full border border-green-500/30">
              <ShieldCheckIcon className="h-3.5 w-3.5 text-green-400 mr-1.5" />
              <span className="text-xs text-green-300 font-medium">Official Instagram API</span>
            </div>
            {!oauthStatus?.google_connected && (
              <span className="text-sm text-yellow-400 flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                Connect Google Drive first
              </span>
            )}
          </div>
        )}
      </div>

      {/* Account List */}
      <div className="space-y-4">
        {/* All Accounts Option */}
        <div
          className={`relative bg-white/5 rounded-2xl p-4 border transition-all cursor-pointer ${
            selectedAccountId === null
              ? 'border-pink-500 shadow-lg shadow-pink-500/20'
              : 'border-white/10 hover:border-white/20'
          }`}
          onClick={() => handleAccountSelect(null)}
        >
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
              <UserCircleIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h4 className="text-white font-medium">All Accounts</h4>
              <p className="text-sm text-gray-400">View posts from all connected accounts</p>
            </div>
            {selectedAccountId === null && (
              <CheckCircleIcon className="h-5 w-5 text-pink-400 ml-auto" />
            )}
          </div>
        </div>

        {/* Individual Accounts */}
        {accounts.map((account) => (
          <div
            key={account.id}
            className={`relative bg-white/5 rounded-2xl p-4 border transition-all cursor-pointer ${
              selectedAccountId === account.id
                ? 'border-pink-500 shadow-lg shadow-pink-500/20'
                : 'border-white/10 hover:border-white/20'
            }`}
            onClick={() => handleAccountSelect(account.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                {/* Profile Picture */}
                <div className="relative">
                  {account.profile_picture_url ? (
                    <img
                      src={account.profile_picture_url}
                      alt={account.username}
                      className="h-16 w-16 rounded-full object-cover border-2 border-white/20"
                    />
                  ) : (
                    <UserCircleIcon className="h-16 w-16 text-gray-400" />
                  )}
                  {selectedAccountId === account.id && (
                    <div className="absolute -bottom-1 -right-1 h-5 w-5 bg-pink-500 rounded-full flex items-center justify-center">
                      <CheckCircleIcon className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>

                {/* Account Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-base font-semibold text-white">@{account.username}</h4>
                    {selectedAccountId === account.id && (
                      <span className="text-xs bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded-full">Selected</span>
                    )}
                  </div>
                  {account.display_name && (
                    <p className="text-sm text-gray-300">{account.display_name}</p>
                  )}
                  
                  {/* Stats */}
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-400">
                    {account.follower_count !== null && (
                      <span>{account.follower_count.toLocaleString()} followers</span>
                    )}
                    <span>{account.synced_posts_count || 0} posts</span>
                    <span>{account.synced_posts_count || 0} synced</span>
                    <span>{account.backed_up_posts_count || 0} backed up</span>
                  </div>

                  {/* Connection Info */}
                  <div className="flex items-center space-x-3 mt-2 text-xs">
                    <span className="text-gray-400">
                      Connected {new Date(account.connected_at).toLocaleDateString()}
                    </span>
                    {account.last_sync && (
                      <span className="text-gray-400">
                        • Last sync: {new Date(account.last_sync).toLocaleDateString()} • Syncs every 10m
                      </span>
                    )}
                    {account.token_expires_at && new Date(account.token_expires_at) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                      <span className="text-yellow-400 flex items-center">
                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                        Token expires soon
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshMutation.mutate(account.id);
                  }}
                  disabled={refreshMutation.isLoading}
                  className="p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Refresh profile"
                >
                  <ArrowPathIcon className={`h-4 w-4 ${refreshMutation.isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Are you sure you want to disconnect @${account.username}?`)) {
                      disconnectMutation.mutate(account.id);
                    }
                  }}
                  disabled={disconnectMutation.isLoading}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Disconnect account"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Empty State */}
        {accounts.length === 0 && (
          <div className="text-center py-8">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-300">No Instagram accounts connected</p>
            <p className="text-sm text-gray-400 mt-1">Connect your Instagram account to start backing up your content</p>
          </div>
        )}

        {/* Upgrade Prompt */}
        {!can_connect_more && subscription_tier !== 'pro' && accounts.length >= limit && (
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-2xl p-4 border border-yellow-500/20">
            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-300">Account limit reached</p>
                <p className="text-xs text-gray-300 mt-1">
                  Upgrade to Pro to connect up to 3 Instagram accounts
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Account Type Selection Modal */}
      {showAccountTypeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 backdrop-blur-sm" onClick={() => setShowAccountTypeModal(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-2xl">
              <button
                onClick={() => setShowAccountTypeModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  What type of Instagram account do you have?
                </h3>
                <p className="text-sm text-gray-300">
                  Vaulto requires a Creator or Business account to access your content.
                </p>
                {/* Official API Badge */}
                <div className="flex justify-center mt-3">
                  <div className="flex items-center px-3 py-1.5 bg-green-500/10 rounded-full border border-green-500/30">
                    <ShieldCheckIcon className="h-4 w-4 text-green-400 mr-2" />
                    <span className="text-xs text-green-300 font-medium">Official Instagram API</span>
                  </div>
                </div>
              </div>

              {/* Loss aversion warning */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-200 text-xs">
                    Every day, creators lose content to hacks, suspensions, and accidental deletions. Protect your work now.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleConnectInstagram('business')}
                  className="w-full flex items-center p-4 bg-gradient-to-r from-pink-500/20 to-purple-600/20 hover:from-pink-500/30 hover:to-purple-600/30 border border-pink-500/30 rounded-xl transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <CheckCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4 text-left">
                    <p className="font-semibold text-white">Creator or Business Account</p>
                    <p className="text-xs text-gray-300">I already have a Professional account</p>
                  </div>
                </button>

                <button
                  onClick={handlePersonalAccountHelp}
                  className="w-full flex items-center p-4 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl transition-all"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-4 text-left">
                    <p className="font-semibold text-white">Personal Account</p>
                    <p className="text-xs text-gray-300">I'll switch to Creator (it's free!)</p>
                  </div>
                </button>

                <button
                  onClick={() => setShowAccountTypeModal(false)}
                  className="w-full p-3 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-App Browser Warning (simple version with arrow) */}
      {showInAppBrowserModal && (
        <div className="fixed inset-0 z-50" onClick={() => setShowInAppBrowserModal(false)}>
          {/* Arrow pointing to 3 dots menu */}
          <div className="absolute top-2 right-4 flex flex-col items-end animate-bounce">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" />
            </svg>
          </div>
          
          {/* Message bubble */}
          <div className="absolute top-16 right-4 left-4 sm:left-auto sm:w-72">
            <div className="bg-gray-900 rounded-2xl shadow-2xl border border-white/20 p-4">
              <p className="text-white text-sm font-medium mb-2">
                Tap the ⋯ menu above
              </p>
              <p className="text-gray-300 text-xs mb-3">
                Then select "Open in {isAndroid() ? 'Chrome' : 'Safari'}" for the best experience
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInAppBrowserModal(false);
                }}
                className="text-gray-400 text-xs hover:text-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Account Info Modal - Switch to Creator */}
      {showPersonalInfoModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900/80 backdrop-blur-sm" onClick={() => setShowPersonalInfoModal(false)} />
            
            <div className="relative inline-block w-full max-w-lg p-6 overflow-hidden text-left align-middle transition-all transform bg-white/10 backdrop-blur-lg border border-white/20 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowPersonalInfoModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  Join Millions of Creators
                </h3>
                <p className="text-sm text-gray-300">
                  Top influencers and serious content creators use Creator accounts for powerful insights and tools. Upgrade in 30 seconds - it's completely free!
                </p>
              </div>

              {/* Step-by-step guide with screenshots */}
              <div className="space-y-4 mb-6">
                {/* Step 1 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">1</span>
                    Instagram will ask you to switch to Professional
                  </p>
                  <img 
                    src="/instagram_switch_to_professional.jpeg" 
                    alt="Instagram switch to professional prompt" 
                    className="w-full max-w-xs mx-auto rounded-lg border border-white/20 shadow-lg"
                  />
                </div>

                {/* Step 2 */}
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <p className="text-sm font-medium text-white mb-3">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-pink-500 text-white text-xs font-bold mr-2">2</span>
                    Select "Creator" when asked
                  </p>
                  <div className="relative w-full max-w-xs mx-auto">
                    <img 
                      src="/instagram_creator_or_business.jpeg" 
                      alt="Instagram creator or business selection" 
                      className="w-full rounded-lg border border-white/20 shadow-lg"
                    />
                    {/* Green indicator circle on Creator option */}
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 border-4 border-green-400 rounded-full animate-pulse"></div>
                    <div className="absolute top-[28%] right-[8%] w-8 h-8 flex items-center justify-center">
                      <CheckCircleIcon className="h-6 w-6 text-green-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Info text */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-200">
                  <strong>Why Creators choose this:</strong> Access to analytics and insights, professional tools, and the ability to automatically backup your content with Vaulto. Plus, you can switch back anytime - your followers and content stay exactly the same.
                </p>
              </div>

              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={handlePersonalAccountContinue}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
                >
                  Continue to Instagram
                </button>

                <button
                  onClick={() => setShowPersonalInfoModal(false)}
                  className="w-full px-4 py-3 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google Drive Required Modal - Shows when user tries to connect Instagram without Google Drive */}
      {showGoogleDriveRequiredModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/70 backdrop-blur-sm" onClick={() => setShowGoogleDriveRequiredModal(false)} />
            
            <div className="relative inline-block overflow-hidden text-left align-bottom transition-all transform bg-gray-900 rounded-2xl shadow-xl sm:my-8 sm:align-middle sm:max-w-md w-full border border-white/20">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">
                    Connect Google Drive First
                  </h3>
                  <button
                    onClick={() => setShowGoogleDriveRequiredModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-5">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 via-green-400 to-yellow-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 mb-4">
                    <img src="/google_drive_icon_.png" alt="Google Drive" className="h-8 w-8" />
                  </div>
                  
                  <p className="text-white text-center text-lg font-medium mb-3">
                    One quick step before Instagram
                  </p>
                  
                  <p className="text-gray-300 text-center text-sm mb-4">
                    To safely backup your Instagram content, you need to connect your Google Drive first. This is where all your precious posts, reels, and stories will be stored.
                  </p>
                </div>

                <div className="mb-5 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <ArrowUpTrayIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-200 text-sm font-medium mb-1">
                        Why Google Drive first?
                      </p>
                      <p className="text-gray-300 text-sm">
                        Your backups go directly to your personal Google Drive. We need this connection set up first so we have a safe place to store your content as soon as you connect Instagram.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowGoogleDriveRequiredModal(false);
                      handleConnectGoogle();
                    }}
                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600 text-white font-semibold rounded-xl transition-all shadow-lg"
                  >
                    Connect Google Drive
                  </button>

                  <button
                    onClick={() => setShowGoogleDriveRequiredModal(false)}
                    className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instagram Explanation Modal - Shows before redirecting to Instagram OAuth */}
      {showInstagramExplanationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black/80 backdrop-blur-sm" onClick={() => setShowInstagramExplanationModal(false)} />
            
            <div className="relative inline-block w-full max-w-md p-5 sm:p-6 overflow-hidden text-left align-middle transition-all transform bg-gray-900 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowInstagramExplanationModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              <div className="text-center mb-5">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/30 mb-4">
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                  </svg>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-3">
                  Connect Instagram
                </h3>
                
                <p className="text-white/90 text-base leading-relaxed">
                  Authorize Vaulto to backup your posts.
                </p>
              </div>

              {/* Permission Screen Preview */}
              <div className="mb-4">
                <p className="text-white/70 text-xs font-medium mb-2 text-center">
                  What you'll see next:
                </p>
                <div className="bg-white rounded-xl p-3 shadow-lg border border-gray-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-pink-500 via-purple-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-xs">Vaulto</p>
                      <p className="text-gray-500 text-[10px]">wants to access your account</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center space-x-2 text-gray-700 text-xs">
                      <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      <span>Access profile information</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-700 text-xs">
                      <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      <span>Access media and content</span>
                    </div>
                  </div>
                  <div className="bg-blue-500 text-white text-center py-1.5 rounded-lg text-xs font-medium">
                    Allow
                  </div>
                  <p className="text-center text-gray-400 text-[10px] mt-1.5">
                    Just tap "Allow" on Instagram's screen
                  </p>
                </div>
              </div>

              {/* Safety Badge */}
              <div className="mb-5 p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
                <div className="flex items-center justify-center space-x-2">
                  <div className="flex items-center space-x-1 bg-green-500/20 px-2 py-1 rounded-full">
                    <CheckCircleIcon className="h-4 w-4 text-green-400" />
                    <span className="text-green-300 text-xs font-medium">Read-only access only</span>
                  </div>
                </div>
                <p className="text-white/70 text-xs text-center mt-2">
                  We NEVER post, like, or modify anything
                </p>
              </div>
              
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={proceedWithInstagramOAuth}
                  className="w-full px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl hover:from-pink-600 hover:to-purple-700 transition-colors font-semibold shadow-lg flex items-center justify-center"
                >
                  Continue to Instagram
                  <ArrowRightIcon className="h-5 w-5 ml-2" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowInstagramExplanationModal(false)}
                  className="w-full px-4 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramAccountsManager;
